// prettier-ignore
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");
		
		// Add promo filtering elements
		this.categoriesList = document.querySelector(".search__categories");
		this.productsList = document.querySelector(".search__products");
		this.productsTitle = document.querySelector(".search__products-title");
		this.noResultsContainer = document.querySelector(".search__no-results");
		this.noResultsMessage = document.querySelector(".search__no-results-message");
		
		this.calcHeight();
		this.setupEventListeners();
	}

	calcHeight() {
		this.results = document.querySelector(".predictive-search__result-group");
		this.resultsTabs = document.querySelector(
			".predictive-search__result-tabs",
		);
		if (this.results) {
			this.results.style.setProperty(
				"--search-height",
				`${parseInt(this.resultsTabs.getBoundingClientRect().bottom)}px`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			this.resetPromoContent(); // Reset promo content when search is cleared
			return;
		}

		// Filter promo content first
		this.filterPromoContent(this.searchTerm);
		
		this.getSearchResults(this.searchTerm);
	}

	// New method to filter promo content
	filterPromoContent(searchTerm) {
		if (!this.promoModal) return;
		
		const term = searchTerm.toLowerCase();
		let hasVisibleCategories = false;
		let hasVisibleProducts = false;

		// Filter categories
		if (this.categoriesList) {
			const categoryItems = this.categoriesList.querySelectorAll('.search__categories-item');
			categoryItems.forEach(item => {
				const link = item.querySelector('.search__categories-link');
				const categoryTitle = link ? link.textContent.toLowerCase() : '';
				
				if (categoryTitle.includes(term)) {
					item.style.display = 'block';
					hasVisibleCategories = true;
				} else {
					item.style.display = 'none';
				}
			});
			
			this.categoriesList.style.display = hasVisibleCategories ? 'block' : 'none';
		}

		// Filter products
		if (this.productsList) {
			const productItems = this.productsList.querySelectorAll('.search__products-item');
			productItems.forEach(item => {
				// Look for product title in various possible selectors
				const productTitle = item.querySelector('.card__heading, .product-title, .card-information__text h3, h3, h4');
				const productTitleText = productTitle ? productTitle.textContent.toLowerCase() : '';
				
				if (productTitleText.includes(term)) {
					item.style.display = 'block';
					hasVisibleProducts = true;
				} else {
					item.style.display = 'none';
				}
			});
			
			this.productsList.style.display = hasVisibleProducts ? 'block' : 'none';
			if (this.productsTitle) {
				this.productsTitle.style.display = hasVisibleProducts ? 'block' : 'none';
			}
		}

		// Show/hide no results message
		if (!hasVisibleCategories && !hasVisibleProducts) {
			this.showNoResults(term);
		} else {
			this.hideNoResults();
		}
	}

	// New method to reset promo content
	resetPromoContent() {
		// Show all categories
		if (this.categoriesList) {
			this.categoriesList.style.display = 'block';
			const categoryItems = this.categoriesList.querySelectorAll('.search__categories-item');
			categoryItems.forEach(item => {
				item.style.display = 'block';
			});
		}
		
		// Show all products
		if (this.productsList) {
			this.productsList.style.display = 'block';
			const productItems = this.productsList.querySelectorAll('.search__products-item');
			productItems.forEach(item => {
				item.style.display = 'block';
			});
		}
		
		if (this.productsTitle) {
			this.productsTitle.style.display = 'block';
		}
		
		this.hideNoResults();
	}

	// New method to show no results in promo
	showNoResults(searchTerm) {
		// Hide content sections
		if (this.categoriesList) this.categoriesList.style.display = 'none';
		if (this.productsList) this.productsList.style.display = 'none';
		if (this.productsTitle) this.productsTitle.style.display = 'none';
		
		// Show no results message
		if (this.noResultsContainer) {
			this.noResultsContainer.style.display = 'block';
			if (this.noResultsMessage) {
				this.noResultsMessage.textContent = `No results found for "${searchTerm}"`;
			}
		}
	}

	// New method to hide no results
	hideNoResults() {
		if (this.noResultsContainer) {
			this.noResultsContainer.style.display = 'none';
		}
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
			this.resetPromoContent();
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			this.resetPromoContent(); // Reset when focusing with empty search
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			// Don't hide promo modal, let filtering handle visibility
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close(true);
			});
		}
	}

	onBlur() {
		this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) {
			this.close(true);
			this.resetPromoContent();
		}
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.close(true);
				this.closeResults(clearSearchTerm);
				this.resetPromoContent();
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			this.calcHeight();
			return;
		}

		// Keep promo modal visible, don't hide it
		// if (this.promoModal && this.classList.contains("search-modal__form")) {
		// 	this.promoModal.classList.add("search__modal--hidden");
		// }

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
			{ signal: this.abortController.signal },
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);
				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				this.calcHeight();
				try {
					colorSwatches();
				} catch (err) {}
			})
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		// Keep promo modal visible when there are search results
		// if (this.promoModal && this.classList.contains("search-modal__form")) {
		// 	this.promoModal.classList.add("search__modal--hidden");
		// }
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length != 0) {
			results[0].classList.add("active");
			this.querySelector(".predictive-search__result-tab").classList.add(
				"active",
			);
			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
			});
		}
	}

	hidePromoBlock() {
		// Don't hide the promo block anymore, let filtering handle it
		// if (this.classList.contains("search-modal__form")) {
		// 	if (this.promoModal) {
		// 		this.promoModal.classList.add("search__modal--hidden");
		// 	}
		// 	if (this.collectionList) {
		// 		this.collectionList.classList.add("hidden");
		// 	}
		// }
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.querySelector("[data-predictive-search-live-region-count-value]")
				.textContent,
		);
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		this.showPromoBlock(); // Always show promo block when opening
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
			this.resetPromoContent(); // Reset when clearing
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		this.showPromoBlock(); // Always show promo block when closing
	}
}

customElements.define("predictive-search", PredictiveSearch);